import { createFormHook, useStore } from '@tanstack/react-form';
import { Button, type ButtonProps } from './button';
import { lazy, type ReactNode } from 'react';
import { useBlocker } from '@tanstack/react-router';
import TextField from './form/text-field';
import TextareaField from './form/textarea-field';
import NumberField from './form/number-field';
import DateTimeField from './form/datetime-field';
import ColorField from './form/color-field';
import FileField from './form/file-field';
import ImageField from './form/image-field';
import SelectTextField from './form/select-text-field';
import SelectNumberField from './form/select-number-field';
import CountryField from './form/country-field';
import RegionField from './form/region-field';
import ProvinceField from './form/province-field';
import ComboboxTextField from './form/combobox-text-field';
import MultiComboboxTextField from './form/multi-combobox-text-field';
import MultiComboboxNumberField from './form/multi-combobox-number-field';
import SwitchField from './form/switch-field';
import { fieldContext, formContext, useFormContext } from './form/shared';
import { cn } from '../lib/utils';
import { Spinner } from './spinner';
import { CheckboxField } from './form/checkbox-field';

const EditorField = lazy(() => import('./form/editor-field'));

export const MAX_SIZE = 4 * 1024 * 1024;

export const downloadFile = (blob: Blob, filename: string) => {
	const link = document.createElement('a');
	link.href = URL.createObjectURL(blob);
	link.download = filename;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
};

export const fileToBase64 = async (file: File | undefined): Promise<string | undefined> =>
	file instanceof File
		? new Promise<string>((resolve, reject) => {
				const reader = new FileReader();
				reader.readAsDataURL(file);
				const handleLoad = () => resolve(reader.result as string);
				reader.addEventListener('load', handleLoad);
				reader.addEventListener('error', reject);
			})
		: undefined;

export const downloadCsvTemplate = (headers: string[], fileName: string) => {
	const csv = headers.join(',');
	// Send charset utf-8 as a header in the download, is the only way to make utf-8 work on excel
	// See https://stackoverflow.com/a/18925211/6261347
	const blob = new Blob(['\ufeff', csv]);
	const downloadLink = document.createElement('a');
	const url = URL.createObjectURL(blob);
	downloadLink.href = url;
	downloadLink.download = `${fileName}.csv`;
	document.body.appendChild(downloadLink);
	downloadLink.click();
	document.body.removeChild(downloadLink);
};

export const getIdOrGenerate = (id: string | undefined): string => (!id || id === '' ? crypto.randomUUID() : id);

export function Title(props: { children: ReactNode; className?: string; size?: 'default' | 'lg' }) {
	return <h1 className={cn(props.size === 'lg' && 'text-3xl font-medium', props.className)}>{props.children}</h1>;
}

export function SubTitle(props: { children: ReactNode; className?: string }) {
	return <h2 className={cn('text-lg font-medium', props.className)}>{props.children}</h2>;
}

export function Paragraph(props: { children: ReactNode; className?: string; as?: 'p' | 'div' }) {
	const Comp = props.as ?? 'p';
	return <Comp className={cn('text-muted-foreground mb-6', props.className)}>{props.children}</Comp>;
}

function SubmitButton({ label, ...props }: ButtonProps & { label: string }) {
	const form = useFormContext();
	return (
		<form.Subscribe selector={(state) => state.isSubmitting}>
			{(isSubmitting) => (
				<Button {...props} disabled={props.disabled ?? isSubmitting}>
					{isSubmitting && <Spinner />}
					{label}
				</Button>
			)}
		</form.Subscribe>
	);
}

function SaveDiscardButtons(props: { className?: string }) {
	const form = useFormContext();
	const { isSubmitting, isDefaultValue } = useStore(form.store, (state) => ({
		isDefaultValue: state.isDefaultValue,
		isSubmitting: state.isSubmitting,
	}));

	useBlocker({
		shouldBlockFn: () => {
			if (isDefaultValue) return false;

			const shouldLeave = confirm('You have unsaved changes, are you sure you want to leave?');
			return !shouldLeave;
		},
		enableBeforeUnload: !isDefaultValue,
	});

	if (isDefaultValue) return null;

	return (
		<div className={cn('flex gap-2', props.className)}>
			<Button
				type="reset"
				variant="secondary"
				onClick={(event) => {
					event.preventDefault();
					form.reset();
					// TODO: We need to double reset to remove all translated campaign texts with the translate feature,
					// is totally a bug on tanstack/form
					setTimeout(() => form.reset());
				}}
			>
				Discard
			</Button>
			<Button disabled={isSubmitting}>{isSubmitting && <Spinner />}Save</Button>
		</div>
	);
}

export const { useAppForm, withForm } = createFormHook({
	fieldComponents: {
		TextField,
		TextareaField,
		NumberField,
		DateTimeField,
		ColorField,
		FileField,
		ImageField,
		SelectTextField,
		SelectNumberField,
		CountryField,
		RegionField,
		ProvinceField,
		ComboboxTextField,
		MultiComboboxTextField,
		MultiComboboxNumberField,
		CheckboxField,
		SwitchField,
		EditorField,
	},
	formComponents: {
		SubmitButton,
		SaveDiscardButtons,
	},
	fieldContext,
	formContext,
});
